import { computed, Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { none, Option } from '@sapphire/result';

import {
  mapSurveyLocalization,
  Qualification,
  SurveyLocaleData,
  AdvancedQualification,
} from '@ps/survey-utils';
import { Undefinable } from '@ps/utils';

import {
  selectSurveyId,
  selectSurvey,
  selectError,
  selectLocationFileUpload,
  selectScorePercentage,
  selectScoreLoading,
  selectScoreError,
  selectFeasibilityTotal,
  selectFeasibilityQuotaGroups,
  selectFeasibilityAudience,
  selectFeasibilityHasQuota,
  selectFeasibilityLoading,
  selectFeasibilityError,
  selectFeasibilityCpi,
  selectFeasibilitySuppliers,
  selectFlexibilityPrices,
  selectFlexibilityPricesError,
} from './audience/survey-creation-audience.selectors';
import {
  selectQualificationsRecord,
  selectQuestionCategories,
  selectIsQuestionCategoriesLoading,
  selectQuestionsRecord,
  selectQuestionsLoadingKey,
} from './reference/survey-creation-reference.selectors';
import { mostRecentCacheKey, surveyQuestionsCacheKey } from '../utils';

@Injectable({ providedIn: 'root' })
export class SurveyCreationSelectorsFacade {
  private readonly _store = inject(Store);

  public readonly surveyId = this._store.selectSignal(selectSurveyId);

  // State holds `Survey | null` (Option is not serializable); the facade is the
  // boundary where that becomes an Option for everything above it.
  private readonly _survey = this._store.selectSignal(selectSurvey);
  public readonly survey = computed(() => Option.from(this._survey()));

  public readonly error = this._store.selectSignal(selectError);

  public readonly locationFileUpload = this._store.selectSignal(
    selectLocationFileUpload,
  );

  //SECTION Score
  public readonly scorePercentage = this._store.selectSignal(
    selectScorePercentage,
  );

  public readonly scoreLoading = this._store.selectSignal(selectScoreLoading);

  public readonly scoreError = this._store.selectSignal(selectScoreError);
  //!SECTION

  //SECTION Feasibility
  public readonly feasibilityTotal = this._store.selectSignal(
    selectFeasibilityTotal,
  );

  public readonly feasibilityQuotaGroups = this._store.selectSignal(
    selectFeasibilityQuotaGroups,
  );

  public readonly feasibilityAudience = this._store.selectSignal(
    selectFeasibilityAudience,
  );

  public readonly feasibilityHasQuota = this._store.selectSignal(
    selectFeasibilityHasQuota,
  );

  public readonly feasibilityLoading = this._store.selectSignal(
    selectFeasibilityLoading,
  );

  public readonly feasibilityError = this._store.selectSignal(
    selectFeasibilityError,
  );

  public readonly feasibilityCpi =
    this._store.selectSignal(selectFeasibilityCpi);

  public readonly feasibilitySuppliers = this._store.selectSignal(
    selectFeasibilitySuppliers,
  );
  //!SECTION

  //SECTION Flexibility Prices
  private readonly _flexibilityPrices = this._store.selectSignal(
    selectFlexibilityPrices,
  );
  public readonly flexibilityPrices = computed(() =>
    Option.from(this._flexibilityPrices()),
  );

  public readonly flexibilityPricesError = this._store.selectSignal(
    selectFlexibilityPricesError,
  );
  //!SECTION

  private readonly _qualificationsRecord = this._store.selectSignal(
    selectQualificationsRecord,
  );

  // Advanced Targeting (definition builder question search) reference data
  public readonly questionCategories = this._store.selectSignal(
    selectQuestionCategories,
  );

  public readonly isQuestionCategoriesLoading = this._store.selectSignal(
    selectIsQuestionCategoriesLoading,
  );

  private readonly _questionsRecord = this._store.selectSignal(
    selectQuestionsRecord,
  );

  private readonly _questionsLoadingKey = this._store.selectSignal(
    selectQuestionsLoadingKey,
  );

  // Selector methods
  public selectQuestions(
    search: string,
    categoryIds: number[],
  ): AdvancedQualification[] {
    return (
      this._questionsRecord()[surveyQuestionsCacheKey(search, categoryIds)]
        ?.data ?? []
    );
  }

  public isQuestionsLoading(search: string, categoryIds: number[]): boolean {
    return (
      this._questionsLoadingKey() ===
      surveyQuestionsCacheKey(search, categoryIds)
    );
  }

  public selectQualifications(
    locale: Undefinable<SurveyLocaleData>,
    recentLocaleWhenNull = false,
  ): Qualification[] {
    return Option.from(locale)
      .map((value) =>
        mapSurveyLocalization(value.countryCode, value.languageShortCode),
      )
      .orElse(() =>
        recentLocaleWhenNull
          ? Option.from(
              mostRecentCacheKey(
                Object.fromEntries(
                  Object.entries(this._qualificationsRecord()).map(
                    ([key, entry]) => [key, entry.cachedAt],
                  ),
                ),
              ),
            )
          : none,
      )
      .mapOr(
        [],
        (key: string) => this._qualificationsRecord()[key]?.data || [],
      );
  }
}
